import { getAdminApiUrl, getEnv, ENVS } from '@adsk/acs-sc-web-platform-api';
import {
  ACCOUNT_OFFERING_TYPES,
  LICENSE_INPUT_TYPES,
  LICENSING_MODELS,
  NO_LICENSE_PLC,
  TRIAL_STATUS,
  USER_ACCOUNT_OFFERING_TYPES,
  USER_SUBSCRIPTION_TYPES,
} from './constants';

export const filterActiveRoles = (response) => {
  return response.filter((role) => {
    return role.status === 'active';
  });
};

export const findIncludedOfferings = (accountOfferings) => {
  return accountOfferings.map((offering) => {
    const allPlcs = offering.includedOfferings.map(
      (offeringIndex) => accountOfferings[offeringIndex].plc
    );
    const includedPlcs = new Set(allPlcs);

    return {
      ...offering,
      includedPlcs: Array.from(includedPlcs),
    };
  });
};

const isInternalLicense = (
  userOfferingsCategory,
  accountOfferingsByCategory
) => {
  return userOfferingsCategory.some(
    (offering) =>
      offering.type === USER_ACCOUNT_OFFERING_TYPES.INTERNAL &&
      accountOfferingsByCategory?.find(
        (accountOffering) =>
          accountOffering.userAssignable &&
          accountOffering.plc === offering.plc &&
          accountOffering.licensingModel === offering.licensingModel
      )
  );
};

const isExternalLicense = (userOfferingsCategory, accountOfferingsByCategory) =>
  userOfferingsCategory.some(
    (offering) => offering.type === USER_ACCOUNT_OFFERING_TYPES.EXTERNAL
  ) && !isInternalLicense(userOfferingsCategory, accountOfferingsByCategory);

const getInternalLicenses = (userOfferingsCategory) =>
  userOfferingsCategory.filter(
    (offering) => offering.type === USER_ACCOUNT_OFFERING_TYPES.INTERNAL
  );

const getExternalLicenses = (userOfferingsCategory) =>
  userOfferingsCategory.filter(
    (offering) => offering.type === USER_ACCOUNT_OFFERING_TYPES.EXTERNAL
  );

const groupOfferingsByCategory = (offerings) => {
  const offeringsByCategory = {};
  offerings.forEach((offering) => {
    offeringsByCategory[offering.category] = [
      ...(offeringsByCategory[offering.category]
        ? offeringsByCategory[offering.category]
        : []),
      { ...offering },
    ];
  });
  return offeringsByCategory;
};

const licensesNotAvailable = (accountOfferingsByCategory, selectedOffering) =>
  accountOfferingsByCategory.length === 1 &&
  !selectedOffering &&
  accountOfferingsByCategory[0].quantity.available === 0;

const userOfferingsContainingLicense = (
  userOfferingsByCategory,
  plc,
  accountOfferings
) =>
  userOfferingsByCategory.filter((userOffering) => {
    const accountOfferingsOfUser = accountOfferings.filter(
      (accountOffering) => {
        return accountOffering.plc === userOffering.plc;
      }
    );

    return accountOfferingsOfUser.some((accountOffering) => {
      return accountOffering.includedPlcs.indexOf(plc) !== -1;
    });
  });

const isOfferingIncluded = (
  accountOfferingsByCategory,
  allAccountOfferings,
  allUserOfferings
) => {
  const result = {};
  const assignableAccountOfferingsByCategory =
    accountOfferingsByCategory.filter((offering) => offering.userAssignable);
  // If category is a toggle
  if (assignableAccountOfferingsByCategory.length === 1) {
    // Get all internal licenses
    const allInternalLicenses = getInternalLicenses(allUserOfferings);

    // Get internal user licenses that contains another license
    const userInternalOfferingsContainingLicense =
      userOfferingsContainingLicense(
        allInternalLicenses,
        assignableAccountOfferingsByCategory[0].plc,
        allAccountOfferings
      );

    // Get all external licenses
    const allExternalLicenses = getExternalLicenses(allUserOfferings);

    // Get external licenses that contains another license
    const userExternalOfferingsContainingLicense =
      userOfferingsContainingLicense(
        allExternalLicenses,
        assignableAccountOfferingsByCategory[0].plc,
        allAccountOfferings
      );

    // If user contains internal license that contains another license
    if (userInternalOfferingsContainingLicense.length) {
      // Search account non user assignable licenses
      const nonUserAssignableLicenses = allAccountOfferings.filter(
        (license) =>
          !license.userAssignable &&
          license.type !== ACCOUNT_OFFERING_TYPES.NONE
      );

      // Search account userAssignable licenses
      const userAssignableLicenses = allAccountOfferings.filter(
        (license) =>
          license.userAssignable && license.type !== ACCOUNT_OFFERING_TYPES.NONE
      );

      // Search internal userAssignable licenses
      const internalUserAssignableLicenses =
        userInternalOfferingsContainingLicense.filter((license) =>
          userAssignableLicenses.some(
            (userAssignableLicense) => userAssignableLicense.plc === license.plc
          )
        );

      // Search internal non userAssignable licenses
      const internalNonUserAssignableLicenses =
        userInternalOfferingsContainingLicense.filter((license) =>
          nonUserAssignableLicenses.some(
            (nonUserAssignableLicense) =>
              nonUserAssignableLicense.plc === license.plc
          )
        );

      // If license including another license is userAssignable
      if (internalUserAssignableLicenses.length) {
        result.internalUserAssignable = true;
        return result;
      }

      // If license including another license is not userAssignable
      if (internalNonUserAssignableLicenses.length) {
        result.internalNonUserAssignable = true;
        result.offeringName = userInternalOfferingsContainingLicense[0].name;
        return result;
      }
    }

    // When  the user only have external licenses
    if (userExternalOfferingsContainingLicense.length) {
      result.externalOnly = true;
      return result;
    }
  }
  // If category is not a toggle
  result.notIncluded = true;
  return result;
};

const isDisabled = (
  userOfferingsCategory,
  accountOfferingsByCategory,
  selectedOffering,
  isUserOfferingIncluded
) => {
  if (isExternalLicense(userOfferingsCategory, accountOfferingsByCategory)) {
    return {
      customizedDisabledTextKey:
        'MEMBER_SUBSCRIPTION_DROPDOWN_EXTERNAL_TOOLTIP_TEXT',
      customizedLabelDescriptionKey:
        'MEMBER_SUBSCRIPTION_DROPDOWN_EXTERNAL_TEXT',
      disabled: true,
    };
  }

  if (isInternalLicense(userOfferingsCategory, accountOfferingsByCategory)) {
    return {
      customizedDisabledTextKey: '',
      customizedLabelDescriptionKey: '',
      disabled: false,
    };
  }

  if (
    accountOfferingsByCategory.length === 1 &&
    licensesNotAvailable(accountOfferingsByCategory, selectedOffering)
  ) {
    return {
      customizedDisabledTextKey: '',
      customizedLabelDescriptionKey: '',
      disabled: true,
    };
  }

  if (isUserOfferingIncluded.internalUserAssignable) {
    return {
      customizedDisabledTextKey:
        'MEMBER_SUBSCRIPTION_DROPDOWN_CONTAINED_INTERNAL_TOOLTIP_TEXT',
      customizedLabelDescriptionKey: '',
      disabled: true,
    };
  }

  if (isUserOfferingIncluded.internalNonUserAssignable) {
    return {
      customizedDisabledTextKey:
        'MEMBER_SUBSCRIPTION_DROPDOWN_CONTAINED_NON_USER_ASSIGNABLE_TOOLTIP_TEXT',
      customizedLabelDescriptionKey: isUserOfferingIncluded.offeringName,
      disabled: true,
    };
  }

  // When  the user only have external licenses
  if (isUserOfferingIncluded.externalOnly) {
    return {
      customizedDisabledTextKey:
        'MEMBER_SUBSCRIPTION_DROPDOWN_EXTERNAL_TOOLTIP_TEXT',
      customizedLabelDescriptionKey:
        'MEMBER_SUBSCRIPTION_DROPDOWN_EXTERNAL_TEXT',
      disabled: true,
    };
  }

  return {
    customizedDisabledTextKey: '',
    customizedLabelDescriptionKey: '',
    disabled: false,
  };
};

export const getSelectedOption = (
  userOfferings,
  offerings,
  currentSelection,
  isUserOfferingIncluded
) => {
  // If the user has a external license and input is dropdown
  const assignableOfferings = offerings.filter(
    (offering) => offering.userAssignable
  );
  if (
    isExternalLicense(userOfferings, offerings) &&
    assignableOfferings.length > 1
  )
    return null;

  if (
    isExternalLicense(userOfferings, offerings) &&
    assignableOfferings.length <= 1
  )
    return assignableOfferings[0];

  const internalLicenses = getInternalLicenses(userOfferings);

  const internalLicensesUserAssignables = internalLicenses.filter((offering) =>
    assignableOfferings.some(
      (assignableOffering) =>
        assignableOffering.plc === offering.plc &&
        assignableOffering.licensingModel === offering.licensingModel
    )
  );

  if (internalLicensesUserAssignables.length !== 0) {
    const selectedOffering = internalLicenses.reduce((prev, current) =>
      prev.ranking < current.ranking ? prev : current
    );

    return (
      offerings.find(
        (offering) =>
          offering.plc === selectedOffering.plc &&
          offering.licensingModel === selectedOffering.licensingModel
      ) || null
    );
  }

  // When the user has no license
  if (currentSelection && currentSelection.plc === NO_LICENSE_PLC) {
    return currentSelection;
  }
  // Non user assignable offerings handling
  if (
    isUserOfferingIncluded?.internalNonUserAssignable ||
    isUserOfferingIncluded?.externalOnly
  ) {
    return assignableOfferings[0];
  }

  return null;
};

export const classifyOfferings = (
  accountOfferings,
  userOfferings,
  currentOfferings
) => {
  const result = {};
  const accountOfferingsWithPlcs = findIncludedOfferings(accountOfferings);

  const accountOfferingsWithTokenFlex = accountOfferingsWithPlcs.filter(
    (offering, index, self) => {
      if (offering.licensingModel === LICENSING_MODELS.RENTAL) {
        const containsTokenFlex = self.some(
          (selfOffering) =>
            offering.plc === selfOffering.plc &&
            selfOffering.licensingModel === LICENSING_MODELS.TOKEN_FLEX &&
            selfOffering.type !== ACCOUNT_OFFERING_TYPES.NONE
        );
        return !containsTokenFlex;
      }
      return offering.type !== ACCOUNT_OFFERING_TYPES.NONE;
    }
  );
  // Account offerings
  const offeringsByCategory = groupOfferingsByCategory(
    accountOfferingsWithTokenFlex
  );

  const userOfferingsByCategory = groupOfferingsByCategory(userOfferings);

  Object.entries(offeringsByCategory).forEach(
    ([category, offeringsCategory]) => {
      const userOfferingsCategory = userOfferingsByCategory[category];

      if (
        userOfferingsCategory &&
        offeringsCategory.some((offering) => offering.userAssignable)
      ) {
        const sortedOfferingsCategory = offeringsCategory
          .filter((offeringCategory) => offeringCategory.userAssignable)
          .sort((a, b) => a.ranking - b.ranking);
        const currentSelectedOption =
          currentOfferings && currentOfferings[category].selectedOffering;

        const isUserOfferingIncluded = isOfferingIncluded(
          offeringsCategory,
          accountOfferingsWithTokenFlex,
          userOfferings
        );

        const selectedOffering = getSelectedOption(
          userOfferingsCategory,
          offeringsCategory,
          currentSelectedOption,
          isUserOfferingIncluded
        );

        const {
          disabled,
          customizedDisabledTextKey,
          customizedLabelDescriptionKey,
        } = isDisabled(
          userOfferingsCategory,
          offeringsCategory,
          selectedOffering,
          isUserOfferingIncluded
        );

        result[category] = {
          title: sortedOfferingsCategory[0].categoryName,
          category,
          categoryRanking: sortedOfferingsCategory[0].categoryRanking,
          customizedDisabledTextKey,
          customizedLabelDescriptionKey,
          options: sortedOfferingsCategory,
          showOffering:
            sortedOfferingsCategory.some(
              (offering) => offering.type !== ACCOUNT_OFFERING_TYPES.NONE
            ) || isUserOfferingIncluded.internalNonUserAssignable,
          showSheets: sortedOfferingsCategory.some(
            (offering) => offering.sheetLimit > 0
          ),
          inputType:
            sortedOfferingsCategory.length === 1
              ? LICENSE_INPUT_TYPES.TOGGLE
              : LICENSE_INPUT_TYPES.DROPDOWN,
          disabledLicense: disabled,
          selectedOffering,
        };
      }
    }
  );

  const sortedClassifiedOfferings = Object.fromEntries(
    Object.entries(result).sort(
      ([, a], [, b]) => a.categoryRanking - b.categoryRanking
    )
  );

  return sortedClassifiedOfferings;
};

export const buildGetAccountCompanyRequest = (accountId, companyId) =>
  `${getAdminApiUrl()}/v1/accounts/${accountId}/companies/${companyId}?fields=name`;

export const buildGetAccountRolesRequest = (accountId, name) =>
  `${getAdminApiUrl()}/v1/accounts/${accountId}/roles?limit=200${
    name ? `&filter[name]=${name}` : ''
  }`;

export const buildGetAccountRoleRequest = (accountId, roleId) =>
  `${getAdminApiUrl()}/v1/accounts/${accountId}/roles/${roleId}?fields=name`;

export const buildUpdateMemberRequest = (projectId, userId, memberData) => [
  `${getAdminApiUrl()}/v1/projects/${projectId}/users/${userId}`,
  {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(memberData),
  },
];

export const buildUpdateAccountMemberRequest = (
  accountId,
  userId,
  memberData
) => [
  `${getAdminApiUrl()}/v1/accounts/${accountId}/users/${userId}`,
  {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(memberData),
  },
];

export const buildResendAccountInvitationRequest = (accountId, userId) => [
  `${getAdminApiUrl()}/v1/accounts/${accountId}/users/${userId}:resend-invitation`,
  {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
  },
];

export const buildAccountRemoveMemberRequest = (accountId, userId) => [
  `${getAdminApiUrl()}/v1/accounts/${accountId}/users/${userId}`,
  {
    method: 'DELETE',
  },
];

export const buildGetAccountUserOfferingsRequest = (userId, accountId) =>
  `${getAdminApiUrl()}/v1/accounts/${accountId}/users/${userId}/offerings`;

export const buildAssignOfferingRequest = (
  userId,
  accountId,
  offeringExternalKey
) => [
  `${getAdminApiUrl()}/v1/accounts/${accountId}/users/${userId}/offerings`,
  {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ offeringExternalKey }),
  },
];

export const buildUnassignOfferingRequest = (
  userId,
  accountId,
  offeringExternalKey
) => [
  `${getAdminApiUrl()}/v1/accounts/${accountId}/users/${userId}/offerings`,
  {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ offeringExternalKey }),
  },
];

export const buildGetAccountUserSheetCountRequest = (accountId, userId) =>
  `${getAdminApiUrl()}/v1/accounts/${accountId}/users/${userId}/sheet-count`;

export const buildGetUserSubscriptionsRequest = (accountId, userId) =>
  `${getAdminApiUrl()}/internal/v1/accounts/${accountId}/users/${userId}/subscriptions`;

export const getUserLimitedOffering = (userOfferings) => {
  const userLimitedOfferings = userOfferings.filter(
    (offering) =>
      offering.type === USER_ACCOUNT_OFFERING_TYPES.INTERNAL &&
      offering.sheetLimit !== 0 &&
      !userOfferings.some(
        (off) =>
          off.category === offering.category &&
          off.sheetLimit === 0 &&
          off.type === USER_ACCOUNT_OFFERING_TYPES.INTERNAL
      )
  );
  userLimitedOfferings.sort((offA, offB) => offB.sheetLimit - offA.sheetLimit);

  return userLimitedOfferings.length > 0 ? userLimitedOfferings[0] : null;
};

export const getSubscriptionMessages = (
  classifiedOfferings,
  userSubscriptions,
  member
) => {
  if (!userSubscriptions || !classifiedOfferings) return null;

  const result = {
    member,
    subscriptionMessages: {},
  };

  const shouldUpdateTrialMessage = (
    subscriptionMessages,
    offeringCategory,
    daysRemaining
  ) =>
    !subscriptionMessages[offeringCategory] ||
    (subscriptionMessages[offeringCategory] &&
      daysRemaining > subscriptionMessages[offeringCategory].daysRemaining);

  userSubscriptions.forEach(
    ({ daysRemaining, offeringCategory, status, type }) => {
      const { disabledLicense, selectedOffering } =
        classifiedOfferings[offeringCategory];
      if (
        (!selectedOffering && !disabledLicense) ||
        (selectedOffering &&
          selectedOffering.plc === NO_LICENSE_PLC &&
          type === USER_SUBSCRIPTION_TYPES.TRIAL)
      ) {
        if (
          shouldUpdateTrialMessage(
            result.subscriptionMessages,
            offeringCategory,
            daysRemaining
          )
        ) {
          result.subscriptionMessages[offeringCategory] = {
            daysRemaining,
            textKey:
              status === TRIAL_STATUS.ACTIVE
                ? 'MEMBER_SUBSCRIPTION_TRIAL_ACTIVE_TEXT'
                : 'MEMBER_SUBSCRIPTION_TRIAL_EXPIRED_TEXT',
          };
        }
      }
    }
  );

  if (Object.keys(result.subscriptionMessages).length) {
    return result;
  }
  return null;
};

export const getContactLink = () => {
  const { QA, STAGING } = ENVS;
  const notProd = [QA, STAGING].includes(getEnv());

  const urlEnv = notProd ? 'stg-' : '';

  return `https://${urlEnv}manage.autodesk.com/user-access/app=provisioning?product=bimacc`;
};
